import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../actions/Button.tsx';
import styles from './ConfirmDialog.module.css';
import { Dialog } from './Dialog.tsx';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  /** What will happen, concretely: "Anna leaves the room and cannot rejoin this screening." */
  readonly consequence: ReactNode;
  readonly confirmLabel: string;
  readonly tone?: 'danger' | 'default' | undefined;
  /** Closes the dialog when the returned promise resolves; stays open if it rejects. */
  readonly onConfirm: () => Promise<unknown> | undefined;
}

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel,
  tone = 'danger',
  onConfirm,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const confirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // The caller reports the failure; keeping the dialog open lets the person retry.
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !pending && onOpenChange(next)}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('ui.cancel')}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={pending} onClick={confirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={styles.consequence}>{consequence}</div>
    </Dialog>
  );
};
